// src/controllers/context/SiteSettingsContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getSystemSetting } from '../services/systemSettingsService';
import { supabase } from '../services/supabase';
import { setActiveContactInfo } from '../services/leadService';

export const DEFAULT_CONTACT_INFO = {
  support_phone: '9214901596',
  support_phone_display: '+91 92149 01596',
  website_url: 'https://nobrokerages.online',
  support_email: 'team@nobrokerages.online',
};

const normalizeWhatsAppPhone = (rawPhone) => {
  const digits = String(rawPhone || '').replace(/\D/g, '');
  if (digits.startsWith('91') && digits.length === 12) return digits.slice(2);
  return digits || DEFAULT_CONTACT_INFO.support_phone;
};

const formatPhoneDisplay = (rawPhone) => {
  const local = normalizeWhatsAppPhone(rawPhone);
  if (local.length === 10) return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  return `+91 ${local}`;
};

const normalizeContactInfo = (value = {}) => {
  const phone = normalizeWhatsAppPhone(value.support_phone || DEFAULT_CONTACT_INFO.support_phone);
  return {
    support_phone: phone,
    support_phone_display: formatPhoneDisplay(phone),
    website_url: (value.website_url || DEFAULT_CONTACT_INFO.website_url).replace(/\/$/, ''),
    support_email: (value.support_email || DEFAULT_CONTACT_INFO.support_email).trim(),
  };
};

const SiteSettingsContext = createContext({
  contactInfo: DEFAULT_CONTACT_INFO,
  loading: true,
  whatsappPhone: DEFAULT_CONTACT_INFO.support_phone,
  phoneDisplay: DEFAULT_CONTACT_INFO.support_phone_display,
  phoneTel: `+91${DEFAULT_CONTACT_INFO.support_phone}`,
  websiteUrl: DEFAULT_CONTACT_INFO.website_url,
  supportEmail: DEFAULT_CONTACT_INFO.support_email,
});

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider = ({ children }) => {
  const [contactInfo, setContactInfo] = useState(() => normalizeContactInfo(DEFAULT_CONTACT_INFO));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const applyContactInfo = (value) => {
      if (!mounted || !value || typeof value !== 'object') return;
      const normalized = normalizeContactInfo(value);
      setContactInfo(normalized);
      setActiveContactInfo(normalized);
    };

    const loadContactInfo = async () => {
      try {
        const value = await getSystemSetting('contact_info', null);
        applyContactInfo(value || DEFAULT_CONTACT_INFO);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadContactInfo();

    const channel = supabase
      .channel('contact-info-settings')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_settings',
          filter: 'key=eq.contact_info',
        },
        (payload) => {
          const nextValue = payload.new?.value;
          if (nextValue) applyContactInfo(nextValue);
        }
      )
      .subscribe();

    const handleFocus = () => {
      loadContactInfo();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleFocus);
      supabase.removeChannel(channel);
    };
  }, []);

  const contextValue = useMemo(() => {
    const whatsappPhone = contactInfo.support_phone;
    return {
      contactInfo,
      loading,
      whatsappPhone,
      phoneDisplay: contactInfo.support_phone_display,
      phoneTel: `+91${whatsappPhone}`,
      websiteUrl: contactInfo.website_url,
      supportEmail: contactInfo.support_email,
    };
  }, [contactInfo, loading]);

  return (
    <SiteSettingsContext.Provider value={contextValue}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
